import { ChangeDetectionStrategy, Component, booleanAttribute, computed, input, signal } from '@angular/core';

export type MerchantAvatarSize = 'xs' | 'sm' | 'md' | 'lg';

const SIZE_CLASSES: Record<MerchantAvatarSize, string> = {
  xs: 'h-7 w-7 text-xs',
  sm: 'h-9 w-9 text-sm',
  md: 'h-10 w-10 text-base',
  lg: 'h-11 w-11 text-base',
};

/**
 * A merchant's Plaid logo, or its first letter when there is no logo or the
 * image fails to load. Decorative: the merchant name is always shown beside it.
 */
@Component({
  selector: 'app-merchant-avatar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'contents' },
  template: `
    <span
      class="flex shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border-muted bg-surface-page font-extrabold"
      [class]="sizeClass()"
      [class.text-text-muted]="!muted()"
      [class.text-text-subtle]="muted()"
      aria-hidden="true"
      data-testid="merchant-avatar"
    >
      @if (showLogo()) {
        <img
          class="h-full w-full object-contain"
          [src]="logoUrl()"
          alt=""
          loading="lazy"
          referrerpolicy="no-referrer"
          (error)="failedSrc.set(logoUrl())"
        />
      } @else {
        {{ initial() }}
      }
    </span>
  `,
})
export class MerchantAvatarComponent {
  readonly name = input.required<string>();
  readonly logoUrl = input<string | null | undefined>(null);
  readonly size = input<MerchantAvatarSize>('sm');
  /** Quieter letter for de-emphasized rows, such as dismissed suggestions. */
  readonly muted = input(false, { transform: booleanAttribute });

  /** The last src that failed, so a new logoUrl gets a fresh attempt. */
  readonly failedSrc = signal<string | null | undefined>(undefined);

  readonly showLogo = computed(() => {
    const src = this.logoUrl();
    return !!src && src !== this.failedSrc();
  });
  readonly initial = computed(() => this.name().trim().charAt(0).toUpperCase() || '?');
  readonly sizeClass = computed(() => SIZE_CLASSES[this.size()]);
}
